import React, {useEffect, useRef, useState} from "react";

import {
    Box,
    createListCollection,
    HStack,
    Icon,
    IconButton,
    InputGroup,
    Input,
    Select,
    Text,
    VStack
} from "@chakra-ui/react";
import {ArrowUpDown, Search, SlidersHorizontal} from "lucide-react";

import {EntityListFilter, IconSelect} from "@/components";
import objectUtil from "@/utils/objectUtil";
import {Skeleton} from "@/components/ui";
import {useLanguage} from "@/contexts/languageContext";

export const EntityListHeaderSkeleton = ({sizes, canSort = true, hasFilters = true, after = null}) => {
    /** Number of button to display */
    const number = [canSort, hasFilters].filter(Boolean).length;
    return <VStack w="100%" h="2.5rem">
        <HStack w={sizes} h="100%" px="3vw" py="0">
            <Skeleton flex={1} h="100%"/>
            <Skeleton h="100%" w={`${number * 2.5}rem`}/>
            {
                after &&
                <Skeleton h="100%">
                    {after}
                </Skeleton>
            }
        </HStack>
    </VStack>;
}

export const EntityListHeader = ({filters, setGetParams, defaultParams, sizes, possibleSorts = [], after = null, displayLanguage, setDisplayLanguage, languageOptions = []}) => {

    const { t, language: uiLanguage } = useLanguage();
    const isMounted = useRef({
        filter: false,
        search: false,
        ...(possibleSorts.length > 0 ? {sort: false} : {})
    });
    const [visibleFilters, setVisibleFilters] = useState(false);
    const [sortBy, setSortBy] = useState(defaultParams.sort);
    const [selectedFilters, setSelectedFilters] = useState(defaultParams.filter);
    const [searchText, setSearchText] = useState(defaultParams.search);
    const [prevSearchState, setPrevSearchState] = useState('');

    const displayLanguageCollection = createListCollection({
        items: languageOptions.map((lang) => ({
            label: lang.label,
            value: lang.value,
        }))
    });

    const possibleSortsCollection = createListCollection({
        items: possibleSorts.map((possibleSort) => ({
            label: possibleSort?.[2],
            value: possibleSort.join('-'),
            type: possibleSort?.[1],
            ordering: possibleSort?.[0],
        }))
    });

    const searchTimeout = useRef();
    useEffect(() => {
        if (isMounted.current.search) {
            clearTimeout(searchTimeout.current);
            searchTimeout.current = setTimeout(() => {
                setGetParams((prevState) => {
                    /** This avoids re-rendering when values is the same */
                    const value = searchText && searchText.length >= parseInt(process.env.REACT_APP_MIN_SEARCH_LENGTH) ? searchText : null
                    if((prevState?.search ?? null) === value){
                        return prevState;
                    }
                    return {
                        ...objectUtil.filterByKeys(prevState, ['search', 'page'], false),
                        ...(value !== null ? { search: value } : {})
                    }
                });
                searchTimeout.current = null;
            }, parseInt(process.env.REACT_APP_SEARCH_TIMEOUT));
        } else {
            isMounted.current.search = true;
        }
        return () => {
            clearTimeout(searchTimeout.current);
        }
    }, [searchText, prevSearchState]);

    useEffect(() => {
        if (isMounted.current.filter) {
            setGetParams((prevState) => ({
                ...objectUtil.filterByKeys(prevState, ['filter', 'page'], false),
                ...(Object.keys(selectedFilters ?? {}).length > 0 ? {filter: selectedFilters} : {}),
            }))
        } else {
            isMounted.current.filter = true;
        }

    }, [selectedFilters]);

    useEffect(() => {
        if (isMounted.current.sort === true) {
            setGetParams((prevState) => ({
                ...prevState,
                sort: sortBy
            }))
        } else if('sort' in isMounted.current){
            isMounted.current.sort = true;
        }
    }, [sortBy]);

    return <VStack w="100%" position="relative">
        <HStack w={sizes} style={{padding: '0 3vw'}}>
            <InputGroup
                flex={1}
                startElement={<Icon as={Search} color="secondary.500" boxSize={5}/>}
            >
                <Input
                    value={searchText}
                    onChange={(e) => setSearchText((prev) => {
                        setPrevSearchState(() => prev);
                        return e?.target?.value ?? ''
                    })}
                    placeholder={`${t('fields.search')} ...`}
                    bg="white"
                    color="secondary.500"
                />
            </InputGroup>
            {
                Object.keys(filters).length > 0 &&
                <IconButton
                    type="button"
                    px="2"
                    h={"100%"}
                    aria-label={t('misc.toggleFilters')}
                    bg={"secondary.500"}
                    onClick={() => setVisibleFilters(prevState => !prevState)}
                >
                    <Icon as={SlidersHorizontal}/>
                </IconButton>
            }
            {
                languageOptions.length > 0
                &&
                <IconSelect
                    flex={0}
                    name="displayLanguage"
                    h={"100%"}
                    icon={<Text fontSize="sm">{displayLanguage === 'fr' ? '🇫🇷' : '🇬🇧'}</Text>}
                    value={[displayLanguage]}
                    onValueChange={e => setDisplayLanguage(e.value[0])}
                    collection={displayLanguageCollection}
                >
                    {
                        displayLanguageCollection.items.map((e) => (
                            <Select.Item item={e} key={`displayLang-${e.value}`}>
                                <HStack>
                                    {e?.label}
                                </HStack>
                                <Select.ItemIndicator/>
                            </Select.Item>
                        ))
                    }
                </IconSelect>
            }
            {
                possibleSortsCollection.items.length > 0
                &&
                <IconSelect
                    flex={0}
                    name="sortBy"
                    h={"100%"}
                    icon={<Icon as={ArrowUpDown} color={"white"}/>}
                    value={[sortBy.join('-')]}
                    onValueChange={e => setSortBy(() => {
                        return e.value[0].split('-');
                    })}
                    collection={possibleSortsCollection}
                >
                    {
                        possibleSortsCollection.items.map((e) => (
                            <Select.Item item={e} key={`sortBy-${e.value}`}>
                                <HStack>
                                    {e?.label ?? `${e?.ordering} ${e?.type}`}
                                </HStack>
                                <Select.ItemIndicator/>
                            </Select.Item>
                        ))
                    }
                </IconSelect>
            }
            {after}
        </HStack>
        {
            visibleFilters &&
            <Box w={sizes} style={{padding: '0 3vw'}}>
                <EntityListFilter options={filters} selectedItems={selectedFilters}
                                  onChange={(e) => setSelectedFilters(() => e)}/>
            </Box>
        }
    </VStack>
}

export default EntityListHeader;
